import {
  createActions, asyncInitialState,
  IDataAction,
  IExtendedState
} from '../../utils';

import { FormViewField } from 'components/FormView';
import { IDataGridOptions, IColDef } from 'components/common/DataGridDevEx/DataGrid.properties';

export interface IFreightData {
  selected: any;
  selectedFilters: any;
  filterRow: {
    formName: string;
    parameters: FormViewField[];
    validate(values: any): any;
  };
  gridOptions: IDataGridOptions;
  Columns: IColDef[];
  Actions: any;
}

export interface IFreightState extends IExtendedState<IFreightData> {
}

export const { types, actions } = createActions(
  {
    setSelected: (row) => ({ row }),
    setEntityView: (defaultView) => defaultView,
    asyncs: {

    }
  },
  'freight');

const RequiredFields = [
  'EntityView', 'TransactionOption', 'MatchOptions'
];

const isNull = (value) => {
  return value === '' || value === undefined || value === null || (Array.isArray(value) && value.length === 0);
};

const initialState = asyncInitialState<IFreightData>({
  selected: null,
  selectedFilters: {
    EntityView: '',
    Warehouse: '',
    FromDate: '',
    ToDate: '',
    MatchOptions: 'Unmatched'
  },
  filterRow: {
    formName: 'SupplierFreightFilters',
    validate: (values) => {
      const errors = {};
      RequiredFields.forEach((item) => {
        if (isNull(values[item])) {
          errors[item] = 'Required';
        }
      });

      return errors;
    },
    parameters: [
      {
        id: 0,
        type: 'EX_LOOKUP_FIELD',
        props: {
          label: 'Entity View',
          name: 'EntityView',
          lookupName: 'EntityView',
          size: 'small',
          required: true
        }
      },
      {
        id: 1,
        type: 'EX_LOOKUP_FIELD',
        props: {
          label: 'Warehouse',
          name: 'Warehouse',
          lookupName: 'WarehouseEntity',
          size: 'small',
          required: false
        }
      },
      {
        id: 2,
        type: 'AUTOCOMPLETE_FIELD',
        props: {
          label: 'Match status',
          name: 'MatchOptions',
          defaultValue: 'Unmatched',
          required: false,
          options: [
            {
              value: 'Unmatched',
              label: 'Unmatched'
            },
            {
              value: 'Matched',
              label: 'Matched'
            }
          ]
        }
      },
      {
        id: 3,
        type: 'DATE_FIELD',
        props: {
          label: 'Date range from',
          name: 'FromDate',
          required: false,
          dateFormat: 'DD/MM/YYYY',
          placeholder: 'DD/MM/YYYY',
          nullable: 'true',
          size: 'small',
          style: {
            display: 'inline-block',
            width: '155px',
            marginRight: '16px'
          }
        }
      },
      {
        id: 4,
        type: 'DATE_FIELD',
        props: {
          label: 'To',
          name: 'ToDate',
          required: false,
          dateFormat: 'DD/MM/YYYY',
          placeholder: 'DD/MM/YYYY',
          nullable: 'true',
          size: 'small',
          style: {
            display: 'inline-block',
            width: '155px',
            marginRight: '16px'
          }
        }
      },
    ] as FormViewField[]
  },
  gridOptions: {
    doubleClickActionTab: '',
    suppressEditDeleteInContextMenu: true,
    rowModelType: 'serverSide',
    cacheBlockSize: 10,
    maxBlocksInCache: 5,
    enableMasterDetailModel: true,
    detailCellRenderer: 'CommentAreaRenderer',
    detailCellRendererParams: {
      getDetailRowData: function (params: { data?: { Comments?: string } }): any {
        return { commentData: params.data.Comments };
      }
    }
  },
  Actions: {
  },
  Columns: [
    { headerName: 'Entered', field: 'Entered', type: 'numericColumn', minWidth: 150, sort: 'desc' },
    {
      headerName: 'GRN',
      field: 'GRN',
      valueGetter: (row) => {
        if (row.data) {
          return row.data.GRNCode;
        }
      },
      minWidth: 120,
      suppressSorting: true
    },
    { headerName: 'Local ship', field: 'LocalShipCode', minWidth: 150, suppressSorting: true },
    {
      headerName: 'Invoice',
      field: 'Invoice',
      minWidth: 120,
      suppressSorting: true,
      hyperlinkParamGetter: (row) => {
        if (row.data && row.data.Invoice) {
          return {
            DocumentNumber: row.data.InvoiceDocumentNumber,
            FieldValue: row.data.Invoice
          };
        } else {
          return null;
        }
      },
      type: 'numericColumn',
      cellRenderer: 'hyperLinkRenderer',
      cellRendererParams: {
        link: '/supplier-invoice-enquiry/invoice-details'
      }
    },

    { headerName: 'Amount', field: 'AmountDisplay', type: 'numericColumn', minWidth: 150, suppressSorting: true, },
    { headerName: 'Reference', field: 'Reference', minWidth: 150, suppressSorting: true, },
    { headerName: 'Source document', field: 'SourceDocument', minWidth: 150, suppressSorting: true, },
    { headerName: 'Comments', field: 'Comments', minWidth: 150, suppressSorting: true, },
  ]
});

export default (state: IFreightState = initialState, action: IDataAction): IFreightState => {
  switch (action.type) {
    case types.setSelected:
      const selected = action.data.row;

      return {
        ...state,
        data: {
          ...state.data,
          selected: selected
        }
      };
    case types.setEntityView:
      const entityView = action.data;

      return {
        ...state,
        data: {
          ...state.data,
          selectedFilters: {
            ...state.data.selectedFilters,
            EntityView: entityView
          }
        }
      };
    default:
      return state;
  }
};

export const selectors = ({
  selected: (state: { supplierEnquiry: { activity: { freight: IFreightState } } }) => state.supplierEnquiry.activity.freight.data.selected,
  gridOptions: (state: { supplierEnquiry: { activity: { freight: IFreightState } } }) => state.supplierEnquiry.activity.freight.data.gridOptions,
  columns: (state: { supplierEnquiry: { activity: { freight: IFreightState } } }) => state.supplierEnquiry.activity.freight.data.Columns,
  Actions: (state: { supplierEnquiry: { activity: { freight: IFreightState } } }) => state.supplierEnquiry.activity.freight.data.Actions,
  filterRow: (state: { supplierEnquiry: { activity: { freight: IFreightState } } }) => state.supplierEnquiry.activity.freight.data.filterRow,
  selectedFilters: (state: { supplierEnquiry: { activity: { freight: IFreightState } } }) => state.supplierEnquiry.activity.freight.data.selectedFilters
});
